import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Award, Link2, Wand2, Shapes, Lightbulb, BookOpen, Landmark, Globe, Waves,
  Sparkles, Info, Check, CheckCheck, X,
} from "lucide-react";

export const ENGINES = [
  { key: "brandable", title: "Brandable", icon: Award, desc: "Distinctive standalone brand names with a strong phonetic identity." },
  { key: "compound", title: "Compound", icon: Link2, desc: "Combines two business concepts (BrightForge, TradeFlow)." },
  { key: "invented", title: "Invented", icon: Wand2, desc: "Brand-new words blended at phonetic boundaries." },
  { key: "abstract", title: "Abstract", icon: Shapes, desc: "Names from deeper concepts, not literal keywords." },
  { key: "metaphor", title: "Metaphor", icon: Lightbulb, desc: "Ideas borrowed from unrelated domains (Amazon = scale, Nike = victory)." },
  { key: "real_world", title: "Real-World", icon: BookOpen, desc: "Real dictionary words used out of context (Slack, Notion, Arc)." },
  { key: "heritage", title: "Heritage", icon: Landmark, desc: "Modern coinages from Latin, Greek and Norse roots." },
  { key: "domain_hack", title: "Domain Hack", icon: Globe, desc: "Two-word combos engineered for domain availability." },
  { key: "sound", title: "Sound", icon: Waves, desc: "Sound symbolism matched to your brand personality." },
];

export const ALL_ENGINE_KEYS = ENGINES.map((e) => e.key);

export const ENGINE_LABELS = {
  brandable: "Brandable", compound: "Compound", invented: "Invented", abstract: "Abstract",
  metaphor: "Metaphor", real_world: "Real-World", heritage: "Heritage", domain_hack: "Domain Hack",
  sound: "Sound", hybrid: "Hybrid", refinement: "Refined",
  // legacy
  semantic: "Compound", fusion: "Invented", phonetic: "Brandable",
};

export function engineLabel(key) {
  return ENGINE_LABELS[key] || (key ? key.charAt(0).toUpperCase() + key.slice(1) : "");
}

function InfoSheet({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[460px] rounded-xl p-0 overflow-hidden gap-0 max-h-[85vh] overflow-y-auto" data-testid="engine-info-sheet">
        <DialogTitle className="sr-only">Naming engines explained</DialogTitle>
        <div className="brand-gradient p-6 text-white">
          <h2 className="text-[20px] font-extrabold tracking-[-0.01em]">Naming engines</h2>
          <p className="text-[13px] text-white/85 mt-1">Choose how Namable creates your names — one, several, or all.</p>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex gap-3">
            <span className="w-9 h-9 rounded-[10px] bg-brand-soft text-brand flex items-center justify-center shrink-0"><Sparkles size={17} /></span>
            <div>
              <p className="text-[14px] font-bold text-ink">Recommended</p>
              <p className="text-[13px] text-ink-muted leading-relaxed">Namable picks the best engines for your idea (the default).</p>
            </div>
          </div>
          {ENGINES.map((e) => {
            const Icon = e.icon;
            return (
              <div key={e.key} className="flex gap-3">
                <span className="w-9 h-9 rounded-[10px] bg-canvas-alt text-ink flex items-center justify-center shrink-0"><Icon size={17} /></span>
                <div>
                  <p className="text-[14px] font-bold text-ink">{e.title}</p>
                  <p className="text-[13px] text-ink-muted leading-relaxed">{e.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function NamingEngineSelector({ selected, onChange, onTrack }) {
  const [info, setInfo] = useState(false);
  const count = selected.length;
  const isRecommended = count === 0;

  const toggle = (key) => {
    const has = selected.includes(key);
    const next = has ? selected.filter((k) => k !== key) : [...selected, key];
    onChange(next);
    onTrack?.("naming_engine_selected", { engine: key, selected: !has, engine_count: next.length });
  };
  const selectAll = () => {
    onChange([...ALL_ENGINE_KEYS]);
    onTrack?.("naming_engine_selected", { engine: "select_all", selected: true, engine_count: ALL_ENGINE_KEYS.length });
  };
  const clearAll = () => {
    onChange([]);
    onTrack?.("naming_engine_selected", { engine: "clear_all", selected: false, engine_count: 0 });
  };
  const chooseRecommended = () => {
    onChange([]);
    onTrack?.("naming_engine_selected", { engine: "recommended", selected: true, engine_count: 0 });
  };

  const status = isRecommended
    ? "Recommended — Namable picks the engines"
    : count === ALL_ENGINE_KEYS.length
    ? "All engines selected"
    : `${count} engine${count === 1 ? "" : "s"} selected`;

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <label className="text-[13px] font-semibold text-ink">Naming Engine</label>
          <button onClick={() => setInfo(true)} data-testid="engine-info-btn" aria-label="About naming engines" className="text-ink-subtle hover:text-brand transition-colors">
            <Info size={15} />
          </button>
        </div>
        <div className="flex items-center gap-3 text-[12px] font-bold">
          <button onClick={selectAll} data-testid="engine-select-all" className="text-brand hover:underline inline-flex items-center gap-1">
            <CheckCheck size={13} /> Select all
          </button>
          <button onClick={clearAll} data-testid="engine-clear-all" className="text-ink-subtle hover:text-ink inline-flex items-center gap-1">
            <X size={13} /> Clear
          </button>
        </div>
      </div>
      <p className="text-[12px] text-ink-subtle mt-1">Choose how Namable creates your names. Select one, combine several, or use all engines.</p>

      {/* Recommended card */}
      <button
        onClick={chooseRecommended}
        data-testid="engine-recommended"
        className={`w-full text-left mt-3 rounded-xl border p-4 flex items-center gap-3 transition-all duration-200 ${
          isRecommended ? "border-brand bg-brand-soft shadow-glow-sm" : "border-[color:var(--hairline-strong)] bg-surface hover:border-brand/40"
        }`}
      >
        <span className={`w-10 h-10 rounded-[12px] flex items-center justify-center shrink-0 ${isRecommended ? "brand-gradient text-white" : "bg-canvas-alt text-ink-muted"}`}>
          <Sparkles size={19} />
        </span>
        <span className="flex-1">
          <span className="block text-[14px] font-bold text-ink">Recommended</span>
          <span className="block text-[12px] text-ink-muted">Let Namable choose the best naming engines for your idea.</span>
        </span>
        {isRecommended && (
          <span className="w-5 h-5 rounded-full bg-brand text-white flex items-center justify-center shrink-0"><Check size={13} strokeWidth={3} /></span>
        )}
      </button>

      {/* 3x3 grid */}
      <div className="grid grid-cols-3 gap-2.5 mt-2.5">
        {ENGINES.map((e) => {
          const Icon = e.icon;
          const active = selected.includes(e.key);
          return (
            <button
              key={e.key}
              onClick={() => toggle(e.key)}
              data-testid={`engine-${e.key}`}
              className={`relative rounded-xl border p-3 flex flex-col items-center text-center gap-2 transition-all duration-200 ${
                active ? "border-brand bg-brand-soft shadow-glow-sm" : "border-[color:var(--hairline-strong)] bg-surface hover:border-brand/40"
              }`}
            >
              {active && (
                <span className="absolute top-1.5 right-1.5 w-4.5 h-4.5 w-[18px] h-[18px] rounded-full bg-brand text-white flex items-center justify-center" data-testid={`engine-check-${e.key}`}>
                  <Check size={11} strokeWidth={3} />
                </span>
              )}
              <span className={`w-9 h-9 rounded-[10px] flex items-center justify-center ${active ? "brand-gradient text-white" : "bg-canvas-alt text-ink-muted"}`}>
                <Icon size={18} />
              </span>
              <span className={`text-[12px] font-semibold ${active ? "text-brand" : "text-ink"}`}>{e.title}</span>
            </button>
          );
        })}
      </div>

      <p className="text-[12px] font-semibold text-ink-muted mt-3" data-testid="engine-status">{status}</p>

      <InfoSheet open={info} onClose={() => setInfo(false)} />
    </div>
  );
}
